import React, { useState } from 'react';
import { Member, PointsHistory } from '../types';
import { X, Plus, Minus, Award, AlertCircle } from 'lucide-react';

interface ManualPointsModalProps {
    member: Member;
    onSave: (amount: number, reason: string, type: 'reward' | 'penalty') => void;
    onClose: () => void;
}

export const ManualPointsModal: React.FC<ManualPointsModalProps> = ({ member, onSave, onClose }) => {
    const [amount, setAmount] = useState<number>(0);
    const [reason, setReason] = useState<string>('');
    const [type, setType] = useState<'reward' | 'penalty'>('reward');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (amount <= 0) {
            alert('积分数量必须大于0');
            return;
        }
        if (!reason.trim()) {
            alert('请填写原因');
            return;
        }
        onSave(amount, reason.trim(), type);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                    <div>
                        <h2 className="text-lg font-bold text-slate-800 dark:text-white">手动调整积分</h2>
                        <p className="text-xs text-slate-500">{member.name} · 当前积分: {member.totalPoints || 0}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors"
                    >
                        <X size={20} className="text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                            调整类型
                        </label>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setType('reward')}
                                className={`flex-1 p-4 rounded-xl border-2 transition-all ${type === 'reward'
                                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                        : 'border-slate-200 dark:border-slate-700 hover:border-green-300'
                                    }`}
                            >
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Plus size={20} className={type === 'reward' ? 'text-green-600' : 'text-slate-400'} />
                                </div>
                                <p className={`text-sm font-bold ${type === 'reward' ? 'text-green-600 dark:text-green-400' : 'text-slate-500'}`}>
                                    奖励积分
                                </p>
                            </button>
                            <button
                                type="button"
                                onClick={() => setType('penalty')}
                                className={`flex-1 p-4 rounded-xl border-2 transition-all ${type === 'penalty'
                                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                        : 'border-slate-200 dark:border-slate-700 hover:border-red-300'
                                    }`}
                            >
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Minus size={20} className={type === 'penalty' ? 'text-red-600' : 'text-slate-400'} />
                                </div>
                                <p className={`text-sm font-bold ${type === 'penalty' ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}`}>
                                    扣除积分
                                </p>
                            </button>
                        </div>
                    </div>

                    {/* Amount Input */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            积分数量
                        </label>
                        <input
                            type="number"
                            min="1"
                            value={amount || ''}
                            onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
                            placeholder="输入积分数量"
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                            autoFocus
                        />
                    </div>

                    {/* Reason Input */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            调整原因
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="例如：完成额外任务、违反规则等..."
                            rows={3}
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                        />
                    </div>

                    {/* Preview */}
                    {amount > 0 && reason && (
                        <div className={`p-4 rounded-xl border-2 ${type === 'reward'
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            }`}>
                            <div className="flex items-start gap-3">
                                {type === 'reward' ? (
                                    <Award size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
                                ) : (
                                    <AlertCircle size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
                                )}
                                <div className="flex-1">
                                    <p className={`text-sm font-bold ${type === 'reward' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                                        }`}>
                                        {type === 'reward' ? '即将奖励' : '即将扣除'} {amount} 积分
                                    </p>
                                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                        {member.name} 的积分将变为: {Math.max(0, (member.totalPoints || 0) + (type === 'reward' ? amount : -amount))}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={amount <= 0 || !reason.trim()}
                        className="w-full bg-slate-900 dark:bg-white dark:text-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-all active:scale-95 shadow-lg shadow-slate-200 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        确认调整
                    </button>
                </form>
            </div>
        </div>
    );
};
